import React from 'react';
import { 
  MapPin, 
  Mail, 
  Phone, 
  Globe, 
  Linkedin, 
  Github,
  Calendar,
  Briefcase,
  GraduationCap,
  Award,
  Building2,
  Languages as LanguagesIcon,
  UserCheck,
  Link,
  Star,
  Code
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { ResumeProtocol } from '../../types/resume';

interface ResumePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  resume: ResumeProtocol;
}

export const ResumePreviewModal: React.FC<ResumePreviewModalProps> = ({ 
  isOpen, 
  onClose, 
  resume 
}) => {
  if (!resume?.personalDetails) {
    return null;
  }

  // Format date to "Month Year" format
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    
    try {
      // Handle YYYY-MM format
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        dateString = `${dateString}-01`;
      }
      
      // Handle ISO date format by extracting just the date part
      if (dateString.includes('T')) {
        dateString = dateString.split('T')[0];
      }
      
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Format to "Month Year"
      const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Resume Preview"
      maxWidth="1000px"
    >
      <div className="p-6 overflow-y-auto">
        {/* Personal Details */}
        <div className="mb-8">
          <h3 className="text-xl font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 border-b border-border-light dark:border-border-dark pb-2">{resume.personalDetails.name}</h3>
          <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
            <div className="flex items-center">
              <Mail className="h-4 w-4 mr-2 text-primary-light dark:text-primary-dark" />
              {resume.personalDetails.email}
            </div>
            {resume.personalDetails.phone && (
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-primary-light dark:text-primary-dark" />
                {resume.personalDetails.phone}
              </div>
            )}
            {resume.personalDetails.location && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-primary-light dark:text-primary-dark" />
                {resume.personalDetails.location.city}, {resume.personalDetails.location.country}
                {resume.personalDetails.location.postalCode && ` ${resume.personalDetails.location.postalCode}`}
              </div>
            )}
          </div>

          {/* Social Links */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            {resume.personalDetails.portfolioWebsite && (
              <a
                href={resume.personalDetails.portfolioWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-light dark:text-primary-dark hover:underline flex items-center"
              >
                <Globe className="h-4 w-4 mr-1" />
                Portfolio
              </a>
            )}
            {resume.personalDetails.linkedinProfile && (
              <a
                href={resume.personalDetails.linkedinProfile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-light dark:text-primary-dark hover:underline flex items-center"
              >
                <Linkedin className="h-4 w-4 mr-1" />
                LinkedIn
              </a>
            )}
            {resume.personalDetails.githubProfile && (
              <a
                href={resume.personalDetails.githubProfile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-light dark:text-primary-dark hover:underline flex items-center"
              >
                <Github className="h-4 w-4 mr-1" />
                GitHub
              </a>
            )}
          </div>
        </div>

        {/* Professional Summary */}
        {resume.professionalSummary && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 border-b border-border-light dark:border-border-dark pb-2">
              Professional Summary
            </h2>
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark whitespace-pre-line">
              {resume.professionalSummary}
            </p>
          </div>
        )}

        {/* Work Experience */}
        {resume.workExperience && resume.workExperience.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center border-b border-border-light dark:border-border-dark pb-2">
              <Briefcase className="h-5 w-5 mr-2 text-primary-light dark:text-primary-dark" />
              Work Experience
            </h2>
            <div className="space-y-6">
              {resume.workExperience.map((exp, index) => (
                <div key={index} className="border-l-2 border-primary-light/30 dark:border-primary-dark/30 pl-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div className="mb-2 sm:mb-0 sm:max-w-[70%]">
                      <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark text-lg">
                        {exp.jobTitle}
                      </h3>
                      <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
                        {exp.companyName}
                      </p>
                    </div>
                    <div className="text-xs text-text-primary-light dark:text-text-primary-dark whitespace-nowrap bg-background-light/40 dark:bg-background-dark/40 px-3 py-1.5 rounded-full">
                      <Calendar className="h-3.5 w-3.5 inline-block mr-1 mb-0.5" />
                      {formatDate(exp.startDate)} - {exp.isCurrentJob ? 'Present' : formatDate(exp.endDate || '')}
                    </div>
                  </div>
                  {exp.location && (
                    <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {exp.location.city}
                      {exp.location.state && `, ${exp.location.state}`}
                      {exp.location.country && `, ${exp.location.country}`}
                    </div>
                  )}
                  {exp.responsibilities && exp.responsibilities.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                        Responsibilities:
                      </p>
                      <ul className="list-disc list-inside text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1 pl-2">
                        {exp.responsibilities.map((resp, idx) => (
                          <li key={idx} className="mb-1">{resp}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {exp.achievements && exp.achievements.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                        Key Achievements:
                      </p>
                      <ul className="list-disc list-inside text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1 pl-2">
                        {exp.achievements.map((achievement, idx) => (
                          <li key={idx} className="mb-1">{achievement}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {resume.education && resume.education.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center border-b border-border-light dark:border-border-dark pb-2">
              <GraduationCap className="h-5 w-5 mr-2 text-primary-light dark:text-primary-dark" />
              Education
            </h2>
            <div className="space-y-5">
              {resume.education.map((edu, index) => (
                <div key={index} className="border-l-2 border-primary-light/30 dark:border-primary-dark/30 pl-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div className="mb-2 sm:mb-0 sm:max-w-[70%]">
                      <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark text-lg">
                        {edu.degree} {edu.fieldOfStudy && `in ${edu.fieldOfStudy}`}
                      </h3>
                      <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
                        {edu.institutionName}
                      </p>
                    </div>
                    <div className="text-xs text-text-primary-light dark:text-text-primary-dark whitespace-nowrap bg-background-light/40 dark:bg-background-dark/40 px-3 py-1.5 rounded-full">
                      <Calendar className="h-3.5 w-3.5 inline-block mr-1 mb-0.5" />
                      {formatDate(edu.startDate)} - {edu.isCurrentEducation ? 'Present' : formatDate(edu.endDate || '')}
                    </div>
                  </div>
                  {edu.location && (
                    <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {edu.location.city}
                      {edu.location.state && `, ${edu.location.state}`}
                      {edu.location.country && `, ${edu.location.country}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projects */}
        {resume.projects && resume.projects.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center border-b border-border-light dark:border-border-dark pb-2">
              <Building2 className="h-5 w-5 mr-2 text-primary-light dark:text-primary-dark" />
              Projects
            </h2>
            <div className="space-y-6">
              {resume.projects.map((project, index) => (
                <div key={index} className="border-l-2 border-primary-light/30 dark:border-primary-dark/30 pl-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div className="mb-2 sm:mb-0 sm:max-w-[70%]">
                      <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark text-lg">
                        {project.projectName}
                      </h3>
                      <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
                        {project.description}
                      </p>
                    </div>
                    <div className="text-xs text-text-primary-light dark:text-text-primary-dark whitespace-nowrap bg-background-light/40 dark:bg-background-dark/40 px-3 py-1.5 rounded-full">
                      <Calendar className="h-3.5 w-3.5 inline-block mr-1 mb-0.5" />
                      {formatDate(project.startDate)} - {project.endDate ? formatDate(project.endDate) : 'Present'}
                    </div>
                  </div>
                  
                  {project.technologiesUsed && project.technologiesUsed.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark flex items-center">
                        <Code className="h-4 w-4 mr-1" />
                        Skills Used:
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {project.technologiesUsed.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark rounded-full text-xs"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {project.projectURL && (
                    <div className="mt-3">
                      <a
                        href={project.projectURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-light dark:text-primary-dark hover:underline inline-flex items-center"
                      >
                        <Link className="h-4 w-4 mr-1" />
                        Project URL
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {resume.certifications && resume.certifications.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center border-b border-border-light dark:border-border-dark pb-2">
              <Award className="h-5 w-5 mr-2 text-primary-light dark:text-primary-dark" />
              Certifications
            </h2>
            <div className="space-y-5">
              {resume.certifications.map((cert, index) => (
                <div key={index} className="border-l-2 border-primary-light/30 dark:border-primary-dark/30 pl-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div className="mb-2 sm:mb-0 sm:max-w-[70%]">
                      <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark text-lg">
                        {cert.certificationName}
                      </h3>
                      <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
                        {cert.issuingOrganization}
                      </p>
                    </div>
                    <div className="text-xs text-text-primary-light dark:text-text-primary-dark whitespace-nowrap bg-background-light/40 dark:bg-background-dark/40 px-3 py-1.5 rounded-full">
                      <Calendar className="h-3.5 w-3.5 inline-block mr-1 mb-0.5" />
                      {formatDate(cert.issueDate)} {cert.expiryDate && `- ${formatDate(cert.expiryDate)}`}
                    </div>
                  </div>
                  
                  {cert.credentialURL && (
                    <div className="mt-2">
                      <a
                        href={cert.credentialURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-light dark:text-primary-dark hover:underline inline-flex items-center"
                      >
                        <Link className="h-4 w-4 mr-1" />
                        Credential Verification
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        {resume.skills && resume.skills.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 border-b border-border-light dark:border-border-dark pb-2">
              <Star className="h-5 w-5 inline-block mr-2 text-primary-light dark:text-primary-dark" />
              Skills
            </h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {resume.skills.map((skill, index) => (
                <div
                  key={index}
                  className="px-3 py-1.5 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full text-sm"
                >
                  <span className="font-medium text-primary-light dark:text-primary-dark">
                    {skill.skillName}
                  </span>
                  {skill.proficiencyLevel && (
                    <span className="text-text-secondary-light dark:text-text-secondary-dark ml-1">
                      • {skill.proficiencyLevel}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        {resume.languages && resume.languages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 border-b border-border-light dark:border-border-dark pb-2">
              <LanguagesIcon className="h-5 w-5 inline-block mr-2 text-primary-light dark:text-primary-dark" />
              Languages
            </h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {resume.languages.map((language, index) => (
                <div
                  key={index}
                  className="px-3 py-1.5 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full text-sm"
                >
                  <span className="font-medium text-primary-light dark:text-primary-dark">
                    {language.languageName}
                  </span>
                  {language.proficiencyLevel && (
                    <span className="text-text-secondary-light dark:text-text-secondary-dark ml-1">
                      • {language.proficiencyLevel}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* References */}
        {resume.references && resume.references.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center border-b border-border-light dark:border-border-dark pb-2">
              <UserCheck className="h-5 w-5 mr-2 text-primary-light dark:text-primary-dark" />
              References
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {resume.references.map((reference, index) => (
                <div key={index} className="border-l-2 border-primary-light/30 dark:border-primary-dark/30 pl-4 py-2">
                  <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark text-base">
                    {reference.referenceName}
                  </h3>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                    {reference.relationship}
                  </p>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                    {reference.contactInfo}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        {resume.additionalInformation && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 border-b border-border-light dark:border-border-dark pb-2">
              Additional Information
            </h2>
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark whitespace-pre-line">
              {resume.additionalInformation}
            </p>
          </div>
        )}

        {/* Update timestamp */}
        {resume.updatedAt && (
          <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-8 flex items-center justify-end">
            <Calendar className="h-3 w-3 mr-1" />
            Last updated: {new Intl.DateTimeFormat('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }).format(new Date(resume.updatedAt))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ResumePreviewModal; 